import * as XLSX from 'xlsx';
import {
	FIELDS_MAPPER,
	IMPORT_COLUMNS,
	type ImportedVehicleRow,
} from './parseImportExcel';
import { applyTextColumnFormat, columnWidths } from './importTemplate';
import type { ImportRowStatus } from './importStatus';
import type { ImportOutcome } from './importOutcome';

const SHEET_NAME = 'Foglio1';

export const FAILED_ROWS_ERROR_COLUMN = 'ERRORE';

export interface FailedRowExport {
	row: ImportedVehicleRow;
	reason: string;
}

/**
 * Da scaricare: tutto cio' che non e' pronto (da correggere, da risolvere,
 * validazione fallita) e le righe pronte che l'import ha rifiutato. Le righe
 * ancora in verifica non hanno ancora un esito.
 */
export const shouldExportRow = (
	status: ImportRowStatus,
	outcome: ImportOutcome | undefined
): boolean => {
	if (outcome?.status === 'error') return true;
	if (outcome?.status === 'success') return false;
	return status === 'invalid' || status === 'ambiguous';
};

const colorCell = (code: string, description?: string): string =>
	description ? `${code} - ${description}` : code;

/** Inverso di `mapExcelRows`: la riga torna com'era scritta nel file. */
const rowToCells = (row: ImportedVehicleRow): string[] =>
	IMPORT_COLUMNS.map((column) => {
		const field = FIELDS_MAPPER[column];
		switch (field) {
			case 'optional_list':
				return row.optional_list.join(', ');
			case 'codice_colore_esterno':
				return colorCell(row.codice_colore_esterno, row.colore_esterno_desc);
			case 'codice_colore_interno':
				return colorCell(row.codice_colore_interno, row.colore_interno_desc);
			default:
				return row[field] ?? '';
		}
	});

/**
 * Stesse colonne del template piu' ERRORE in fondo: al re-import il parser
 * legge solo le colonne che conosce.'.
 */
export const buildFailedRowsSheet = (
	items: FailedRowExport[]
): XLSX.WorkSheet => {
	const headers = [...IMPORT_COLUMNS, FAILED_ROWS_ERROR_COLUMN];
	const sheet = XLSX.utils.aoa_to_sheet([
		headers,
		...items.map(({ row, reason }) => [...rowToCells(row), reason]),
	]);

	applyTextColumnFormat(sheet, items.length);
	sheet['!cols'] = columnWidths(headers);

	return sheet;
};

export const failedRowsFileName = (sourceFileName: string): string =>
	`${sourceFileName.replace(/\.xlsx?$/i, '')} - da correggere.xlsx`;

export const downloadFailedRows = (
	items: FailedRowExport[],
	sourceFileName: string
) => {
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(
		workbook,
		buildFailedRowsSheet(items),
		SHEET_NAME
	);
	XLSX.writeFile(workbook, failedRowsFileName(sourceFileName));
};
